import { Subject } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';

import { ObservableAndState } from '../utils/observable_and_state';
import { createStateSelector } from '../utils/selectors';
import { CommonAccountDetails, CommonAccountState } from './types';
import {
  mapAssignedProviderToSelectedProvider,
  ProviderPortalAssignedProvider,
  SelectedProvider,
  VendorAssignedProvider,
} from './types/account';

function checkAccountStateIsUnchanged(prev: CommonAccountState, curr: CommonAccountState) {
  return (
    prev.selectedProvider === curr.selectedProvider &&
    prev.selectedVendor === curr.selectedVendor &&
    prev.details === curr.details &&
    prev.requestState === curr.requestState
  );
}

function selectSelectedProviderData(
  accountState: Readonly<CommonAccountState>,
): SelectedProvider | null {
  if (!accountState?.details?.assignments?.length) {
    return null;
  }

  const { selectedProvider } = accountState;
  const assignedProviders = accountState.details.assignments;
  const firstProvider = assignedProviders[0];

  // if we do not have selected provider, we return first 1 as selected
  if (!selectedProvider) {
    return mapAssignedProviderToSelectedProvider(firstProvider);
  }

  // we search for selected provider
  const providerData = assignedProviders?.find((item) => item.provider_id === selectedProvider);

  // if we do not have selected provider, we return first 1 as selected
  return mapAssignedProviderToSelectedProvider(providerData || firstProvider);
}
function selectSelectedProviderIdData(accountState: Readonly<CommonAccountState>): number | null {
  return selectSelectedProviderData(accountState)?.providerId ?? null;
}

function selectAssignedProvidersByVendor(
  accountState: Readonly<CommonAccountState>,
): Map<number, VendorAssignedProvider> {
  return accountState?.details?.assignedProvidersByVendor ?? new Map();
}

function selectVendorPermissions(accountState: Readonly<CommonAccountState>): number[] | undefined {
  return accountState?.vendorPermissions;
}

function selectAssignedProviders(
  accountState: Readonly<CommonAccountState>,
): ProviderPortalAssignedProvider[] {
  return accountState?.details?.assignments ?? [];
}

function selectAccountDetails(
  accountState: Readonly<CommonAccountState>,
): CommonAccountDetails | undefined {
  return accountState?.details;
}

export type CustomAccountDetailsSelector<T> = (accountDetails: CommonAccountDetails) => T;
export type CustomSelectedProviderDataSelector<T> = (providerData: SelectedProvider) => T;

export class AccountService {
  private store: CommonAccountState;
  private storeSubject: Subject<CommonAccountState>;
  private changeProviderSubject: Subject<number>;
  private showNotificationSubject: Subject<string>;
  private refreshAccountInfoSubject: Subject<void>;

  constructor(accountStateObservable: ObservableAndState<CommonAccountState>) {
    this.storeSubject = new Subject<CommonAccountState>();
    this.changeProviderSubject = new Subject<number>();
    this.showNotificationSubject = new Subject<string>();
    this.refreshAccountInfoSubject = new Subject<void>();

    const { observable, state } = accountStateObservable;

    this.store = state;
    observable
      .pipe(distinctUntilChanged(checkAccountStateIsUnchanged))
      .subscribe((accountState: CommonAccountState) => {
        this.storeSubject.next((this.store = accountState));
      });
  }

  public getAllData$(): ObservableAndState<CommonAccountState> {
    return createStateSelector(this.storeSubject, this.store, (state) => state);
  }

  public getAccountDetails$(): ObservableAndState<CommonAccountDetails> {
    return createStateSelector(
      this.storeSubject,
      this.store,
      (state) => selectAccountDetails(state) as CommonAccountDetails,
    );
  }

  public getSelectedProviderData$(): ObservableAndState<SelectedProvider> {
    return createStateSelector(
      this.storeSubject,
      this.store,
      (state) => selectSelectedProviderData(state) as SelectedProvider,
    );
  }

  public getSelectedProviderId$(): ObservableAndState<number> {
    return createStateSelector(
      this.storeSubject,
      this.store,
      (state) => selectSelectedProviderIdData(state) as number,
    );
  }

  public getAssignedProviders$(): ObservableAndState<ProviderPortalAssignedProvider[]> {
    return createStateSelector(this.storeSubject, this.store, selectAssignedProviders);
  }

  public getAssignedProvidersByVendor$(): ObservableAndState<Map<number, VendorAssignedProvider>> {
    return createStateSelector(this.storeSubject, this.store, selectAssignedProvidersByVendor);
  }

  public getVendorPermissions$(): ObservableAndState<number[] | undefined> {
    return createStateSelector(this.storeSubject, this.store, selectVendorPermissions);
  }

  public createAccountDetailsSelector$<T>(
    selector: CustomAccountDetailsSelector<T>,
  ): ObservableAndState<T> {
    return createStateSelector<CommonAccountState, T>(
      this.storeSubject,
      this.store,
      (state: CommonAccountState) => {
        return selector(selectAccountDetails(state) as CommonAccountDetails);
      },
    );
  }

  public createSelectedProviderDataSelector$<T>(
    selector: CustomSelectedProviderDataSelector<T>,
  ): ObservableAndState<T> {
    return createStateSelector<CommonAccountState, T>(
      this.storeSubject,
      this.store,
      (state: CommonAccountState) => {
        return selector(selectSelectedProviderData(state) as SelectedProvider);
      },
    );
  }

  public getShowNotificationSubject$(): Subject<string> {
    return this.showNotificationSubject;
  }

  public getChangeProviderSubject$(): Subject<number> {
    return this.changeProviderSubject;
  }

  public getRefreshAccountInfoSubject$(): Subject<void> {
    return this.refreshAccountInfoSubject;
  }

  public requestAccountInfoRefresh(): void {
    this.refreshAccountInfoSubject.next();
  }
}
